import { useCallback, useEffect, useRef, useState } from 'react';
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import Footer from '@/components/Footer';
import {
  AgenticApps, Build, ChatApps, Faq, Film, FilmLightbox, Org, Overview, Pricing, Private, Stats,
} from '@/components/landing/Sections';
import { LandingLangProvider, useLanding } from '@/components/landing/i18n';
import '@/components/landing/landing.css';

function LandingPage() {
  const { lang } = useLanding();
  const [filmOpen, setFilmOpen] = useState(false);
  const closeFilm = useCallback(() => setFilmOpen(false), []);
  const root = useRef<HTMLDivElement>(null);

  // Apple-style motion: headings fade in once as they enter; everything else is static.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const items = Array.from(el.querySelectorAll<HTMLElement>('.r:not(.in)'));
    if (!('IntersectionObserver' in window)) {
      items.forEach((n) => n.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach((n) => io.observe(n));
    return () => io.disconnect();
  });

  // One scroll-linked moment: .lp-scrub scales 0.96 → 1 while scrolling in (reverses on the way up).
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const vh = window.innerHeight;
      root.current?.querySelectorAll<HTMLElement>('.lp-scrub').forEach((n) => {
        const top = n.getBoundingClientRect().top;
        const p = Math.min(1, Math.max(0, (vh - top) / (vh * 0.6)));
        n.style.transform = `scale(${(0.96 + 0.04 * p).toFixed(4)})`;
      });
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  });

  return (
    <div ref={root} className="lp lp-js" data-lang={lang} lang={lang} id="top">
      <Navbar />
      <main>
        <Hero onWatchFilm={() => setFilmOpen(true)} />
        <Stats />
        <Film />
        <Overview />
        {/* Chat Apps (and your own, via Create App) → Agentic Apps → My Org, which rolls both out to a team. */}
        <ChatApps />
        <Build />
        <AgenticApps />
        <Org />
        <Private />
        <Pricing />
        <Faq />
      </main>
      <Footer />
      <FilmLightbox open={filmOpen} onClose={closeFilm} />
    </div>
  );
}

export default function Home() {
  return (
    <LandingLangProvider>
      <LandingPage />
    </LandingLangProvider>
  );
}
